import { useState, useEffect, useCallback, useMemo, useRef, type CSSProperties, type MouseEvent as ReactMouseEvent, type ReactNode } from 'react'
import type { ChangeEvent, MouseEvent } from 'react'
import { useLocation } from 'react-router-dom'
import {
  ChevronDownIcon,
  ChevronUpDownIcon,
  ChevronUpIcon,
  MagnifyingGlassIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  ClockIcon,
  CheckIcon,
} from '@heroicons/react/24/outline'
import { searchEmails, checkProcessedEmails } from '@/services/emailSearch'
import { startBulkEmailProcess } from '@/services/jobs'
import { getEmailRules } from '@/services/emailRules'
import { useEmailSearchStore } from '@/store/emailSearchStore'
import { useBackgroundJobsStore } from '@/store/backgroundJobsStore'
import { toast } from '@/store/toastStore'
import type {
  EmailSearchRequest,
  EmailSearchResult,
  GlobalFilters,
  RuleQuickFilter,
} from '@/types/emailSearch'
import type { EmailRule } from '@/types/emailRule'
import DateInput from '@/components/DateInput'
import InboxFilterInput from '@/components/InboxFilterInput'
import { useMonitoredInboxes } from '@/hooks/useMonitoredInboxes'
import { completeInboxFilterTokens } from '@/utils/inboxFilter'
import { foldForSearch } from '@/utils/foldForSearch'
import { formatLocalDateTime } from '@/utils/dateFormat'
import { useMaxMd } from '@/hooks/useMaxMd'
import {
  ALL_COLUMN_KEYS,
  EMAIL_SEARCH_COLUMN_LABELS,
  EMAIL_SEARCH_DEFAULT_SORT,
  EMAIL_SEARCH_DEFAULT_WIDTHS,
  EMAIL_SEARCH_FIELD_DEFINITIONS,
  EMAIL_SEARCH_RESULTS_PAGE_KEY,
  buildEmailSearchFilterFields,
  fixedColumnStyle,
  isEmailColumnSortable,
  migrateLegacyColumnWidths,
  type EmailSearchColumnKey,
} from '@/pages/emailSearchTableConfig'
import {
  ActiveFilterPillBar,
  ColumnPicker,
  ColumnResizeHandle,
  FilterBuilderDrawer,
  HoverHelp,
  SearchInput,
  SortBuilder,
  TablePageShell,
  TableToolbar,
  applySorts,
  countFilterConditions,
  generateFilterPills,
  getDefaultColumnVisibility,
  getVisibleFieldKeys,
  loadColumnVisibility,
  removeFilterPill,
  saveColumnVisibility,
  useColumnWidths,
  type ColumnVisibility,
  type SortDirection,
  type SortSpec,
} from '@/tableControls'
import {
  filterEmailRowsByText,
  filterEmailRowsWithTree,
  isEmailNewOnlyStatusFilter,
} from '@/utils/emailSearchResultFilters'
import { emailsToForwardFromDialog, resolveForwardEmailScope, shouldIncludePreviouslyReviewed } from '@/utils/emailSearchForward'

const INTEGRATION_DEST_ORDER = ['qbo', 'meez'] as const

function EmailSortableHeader({
  label,
  sortKey,
  sortDirection,
  onSort,
  className = '',
  title,
  columnKey,
  style,
  onStartResize,
  showResize = true,
}: {
  label: ReactNode
  sortKey: string
  sortDirection: SortDirection | null
  onSort: (key: string) => void
  className?: string
  title?: string
  columnKey: string
  style?: CSSProperties
  onStartResize?: (key: string, event: ReactMouseEvent) => void
  showResize?: boolean
}) {
  const isSorted = sortDirection !== null

  return (
    <th
      className={`sortable relative ${isSorted ? 'sorted' : ''} ${className}`}
      style={style}
      title={title}
      onClick={() => onSort(sortKey)}
    >
      <span className="inline-flex items-center gap-1 pr-2">
        {label}
        <span className="sort-indicator">
          {sortDirection === 'asc' ? (
            <ChevronUpIcon className="h-3 w-3" />
          ) : sortDirection === 'desc' ? (
            <ChevronDownIcon className="h-3 w-3" />
          ) : (
            <ChevronUpDownIcon className="h-3 w-3" />
          )}
        </span>
      </span>
      {showResize && onStartResize ? (
        <ColumnResizeHandle columnKey={columnKey} onStartResize={onStartResize} />
      ) : null}
    </th>
  )
}

/** Gmail search can return the same RFC Message-ID once per inbox; keep a single row for UI and selection. */
function dedupeEmailSearchResultsByMessageId(results: EmailSearchResult[]): EmailSearchResult[] {
  const seen = new Set<string>()
  const out: EmailSearchResult[] = []
  for (const r of results) {
    if (seen.has(r.message_id)) continue
    seen.add(r.message_id)
    out.push(r)
  }
  return out
}

export default function EmailSearch() {
  const store = useEmailSearchStore()
  const location = useLocation()

  useEffect(() => {
    useEmailSearchStore.getState().expireSessionIfStale()
  }, [])

  useEffect(() => {
    const id = window.setInterval(() => {
      useEmailSearchStore.getState().expireSessionIfStale()
    }, 60_000)
    return () => window.clearInterval(id)
  }, [])

  const searchMode = store.searchMode
  const setSearchMode = store.setSearchMode
  const globalFilters = store.globalFilters
  const setGlobalFilters = store.setGlobalFilters
  const manualFilters = store.manualFilters
  const setManualFilters = store.setManualFilters
  const rawGmailQuery = store.rawGmailQuery
  const setRawGmailQuery = store.setRawGmailQuery

  const selectedRuleIds = useMemo(() => new Set(store.selectedRuleIds), [store.selectedRuleIds])
  const setSelectedRuleIds = useCallback(
    (ids: Set<number>) => {
      store.setSelectedRuleIds(Array.from(ids))
    },
    [store]
  )
  const quickFilter = store.quickFilter
  const setQuickFilter = store.setQuickFilter
  const ruleSearch = store.ruleSearch
  const setRuleSearch = store.setRuleSearch

  const results = store.results
  const queryUsed = store.queryUsed
  const lastSearchCompletedAt = store.lastSearchCompletedAt
  const beginNewSearch = store.beginNewSearch

  const resultFilterTree = store.resultFilterTree
  const setResultFilterTree = store.setResultFilterTree
  const resultTextSearch = store.resultTextSearch
  const setResultTextSearch = store.setResultTextSearch

  const selectedResultMessageIds = store.selectedResultMessageIds
  const setSelectedResultMessageIds = store.setSelectedResultMessageIds
  const selectedIds = useMemo(() => new Set(selectedResultMessageIds), [selectedResultMessageIds])

  const patchSelectedIds = useCallback(
    (updater: (prev: Set<string>) => Set<string>) => {
      const prev = new Set(useEmailSearchStore.getState().selectedResultMessageIds)
      setSelectedResultMessageIds(Array.from(updater(prev)))
    },
    [setSelectedResultMessageIds]
  )

  const monitoredInboxes = useMonitoredInboxes()

  const [loading, setLoading] = useState(false)
  const [processing, setProcessing] = useState(false)
  const registerServerJob = useBackgroundJobsStore((s) => s.registerServerJob)

  /** Row index in `filteredResults` for the next shift+click range; each range unions into `selectedIds`. */
  const selectionAnchorIndexRef = useRef<number | null>(null)
  /**
   * After shift+range selection (mousedown preventDefault), React still delivers `change`
   * with checked=false for the endpoint row. Hold this ref until that event is ignored.
   */
  const suppressCheckboxChangeForMessageIdRef = useRef<string | null>(null)

  const [emailRules, setEmailRules] = useState<EmailRule[]>([])
  const [rulesLoading, setRulesLoading] = useState(true)
  const processedInfo = store.processedInfo
  const setProcessedInfo = store.setProcessedInfo
  const [showForwardDialog, setShowForwardDialog] = useState(false)
  const [forwardNewOnly, setForwardNewOnly] = useState(true)
  /** When set (e.g. row ✈ with mixed scope), Forward dialog stats use this list instead of checkbox selection alone. */
  const [forwardDialogOverride, setForwardDialogOverride] = useState<EmailSearchResult[] | null>(null)
  const [showResultFilters, setShowResultFilters] = useState(false)
  const [showSort, setShowSort] = useState(false)
  const [showColumns, setShowColumns] = useState(false)
  const [sortSpecs, setSortSpecs] = useState<SortSpec[]>(EMAIL_SEARCH_DEFAULT_SORT)
  const [columnVisibility, setColumnVisibility] = useState<ColumnVisibility>(() =>
    loadColumnVisibility(EMAIL_SEARCH_RESULTS_PAGE_KEY, EMAIL_SEARCH_FIELD_DEFINITIONS)
  )

  useEffect(() => {
    migrateLegacyColumnWidths()
  }, [])

  useEffect(() => {
    async function loadRules() {
      try {
        const response = await getEmailRules()
        setEmailRules(response.rules)
      } catch (err) {
        console.error('Failed to load email rules:', err)
      } finally {
        setRulesLoading(false)
      }
    }
    loadRules()
  }, [])

  const refreshProcessedInfo = useCallback(async () => {
    if (results.length > 0) {
      const messageIds = results.map(r => r.message_id)
      const processedData = await checkProcessedEmails(messageIds)
      setProcessedInfo(processedData.processed)
    }
  }, [results, setProcessedInfo])

  useEffect(() => {
    refreshProcessedInfo()
  }, [results, refreshProcessedInfo])

  useEffect(() => {
    refreshProcessedInfo()
  }, [location, refreshProcessedInfo])

  useEffect(() => {
    if (!isEmailNewOnlyStatusFilter(resultFilterTree)) return
    patchSelectedIds((prev) => {
      const next = new Set<string>()
      for (const id of prev) {
        if (!processedInfo[id]) next.add(id)
      }
      return next.size === prev.size ? prev : next
    })
  }, [resultFilterTree, processedInfo, patchSelectedIds])

  const uniqueDestinations = useMemo(() => {
    const dests = new Set<string>()
    emailRules.forEach((r) => r.destinations.forEach((d) => dests.add(d)))
    return Array.from(dests).sort()
  }, [emailRules])

  const filteredRules = useMemo(() => {
    let filtered = emailRules.filter((r) => !r.is_negative_rule)

    if (quickFilter) {
      switch (quickFilter) {
        case 'confirmed':
          filtered = filtered.filter(r => r.status === 'confirmed')
          break
        case 'confirmed-qbo':
          filtered = filtered.filter(r => r.status === 'confirmed' && r.destinations.includes('qbo'))
          break
        case 'confirmed-meez':
          filtered = filtered.filter(r => r.status === 'confirmed' && r.destinations.includes('meez'))
          break
      }
    }

    if (ruleSearch.trim()) {
      const search = foldForSearch(ruleSearch.trim())
      filtered = filtered.filter(rule =>
        foldForSearch(rule.vendor_name).includes(search) ||
        foldForSearch(rule.from_filter).includes(search) ||
        foldForSearch(rule.subject_contains).includes(search) ||
        foldForSearch(rule.notes).includes(search)
      )
    }

    return filtered
  }, [emailRules, quickFilter, ruleSearch])

  const rulesForDisplay = useMemo(() => {
    const selected: EmailRule[] = []
    const rest: EmailRule[] = []
    for (const r of filteredRules) {
      if (selectedRuleIds.has(r.id)) {
        selected.push(r)
      } else {
        rest.push(r)
      }
    }
    return [...selected, ...rest]
  }, [filteredRules, selectedRuleIds])

  const clearRuleSelection = useCallback(() => {
    store.setSelectedRuleIds([])
    setQuickFilter(null)
  }, [store])

  function handleQuickFilterChange(filter: RuleQuickFilter) {
    if (quickFilter === filter) {
      setQuickFilter(null)
      setSelectedRuleIds(new Set())
    } else {
      setQuickFilter(filter)
      const matchingRules =
        filter === 'confirmed'
          ? emailRules.filter(r => r.status === 'confirmed' && !r.is_negative_rule)
          : filter === 'confirmed-qbo'
            ? emailRules.filter(r => r.status === 'confirmed' && !r.is_negative_rule && r.destinations.includes('qbo'))
            : emailRules.filter(r => r.status === 'confirmed' && !r.is_negative_rule && r.destinations.includes('meez'))
      setSelectedRuleIds(new Set(matchingRules.map(r => r.id)))
    }
  }

  function toggleRuleSelection(ruleId: number) {
    const newSelected = new Set(selectedRuleIds)
    if (newSelected.has(ruleId)) {
      newSelected.delete(ruleId)
    } else {
      newSelected.add(ruleId)
    }
    setSelectedRuleIds(newSelected)
    setQuickFilter(null)
  }

  const uniqueResultInboxes = useMemo(() => {
    const inboxes = new Set<string>()
    results.forEach((r) => {
      if (r.inbox) inboxes.add(r.inbox)
    })
    return Array.from(inboxes).sort()
  }, [results])

  const emailFilterFields = useMemo(
    () =>
      buildEmailSearchFilterFields(
        uniqueResultInboxes.map((inbox) => ({ value: inbox, label: inbox }))
      ),
    [uniqueResultInboxes]
  )

  const filteredResults = useMemo(
    () => filterEmailRowsWithTree(results, resultFilterTree, emailFilterFields, processedInfo),
    [results, resultFilterTree, emailFilterFields, processedInfo]
  )

  const textFilteredResults = useMemo(
    () => filterEmailRowsByText(filteredResults, resultTextSearch),
    [filteredResults, resultTextSearch]
  )

  const sortedResults = useMemo(
    () => applySorts(textFilteredResults, sortSpecs, EMAIL_SEARCH_FIELD_DEFINITIONS),
    [textFilteredResults, sortSpecs]
  )

  const getSortDirection = useCallback(
    (field: string): SortDirection | null => {
      const spec = sortSpecs.find((entry) => entry.field === field)
      return spec?.direction ?? null
    },
    [sortSpecs]
  )

  const handleRequestSort = useCallback((field: string) => {
    setSortSpecs((prev) => {
      const existing = prev.find((entry) => entry.field === field)
      if (existing) {
        return [{ field, direction: existing.direction === 'asc' ? 'desc' : 'asc' }]
      }
      return [{ field, direction: 'asc' }]
    })
  }, [])

  const visibleColumnKeys = useMemo(
    () => getVisibleFieldKeys(EMAIL_SEARCH_FIELD_DEFINITIONS, columnVisibility),
    [columnVisibility]
  )

  const visibleColumnCount = visibleColumnKeys.length

  const handleColumnVisibilityChange = useCallback((visibility: ColumnVisibility) => {
    setColumnVisibility(visibility)
    saveColumnVisibility(EMAIL_SEARCH_RESULTS_PAGE_KEY, visibility)
  }, [])

  const { getWidth, startResize } = useColumnWidths({
    pageKey: EMAIL_SEARCH_RESULTS_PAGE_KEY,
    columnKeys: [...ALL_COLUMN_KEYS],
    defaults: EMAIL_SEARCH_DEFAULT_WIDTHS,
  })
  const maxMd = useMaxMd()
  const allowColumnResize = !maxMd

  const visibleTableWidth = useMemo(
    () => visibleColumnKeys.reduce((sum, key) => sum + getWidth(key), 0),
    [getWidth, visibleColumnKeys]
  )

  const tableStyle = useMemo((): CSSProperties | undefined => {
    if (!allowColumnResize) return undefined
    return {
      width: visibleTableWidth,
      minWidth: visibleTableWidth,
      tableLayout: 'fixed',
    }
  }, [allowColumnResize, visibleTableWidth])

  const headerStyle = useCallback(
    (columnKey: string): CSSProperties | undefined => {
      if (!allowColumnResize) return undefined
      return {
        ...fixedColumnStyle(getWidth(columnKey)),
        position: 'sticky',
        top: 0,
        zIndex: 10,
      }
    },
    [allowColumnResize, getWidth]
  )

  const cellStyle = useCallback(
    (columnKey: string): CSSProperties | undefined => {
      if (!allowColumnResize) return undefined
      return fixedColumnStyle(getWidth(columnKey))
    },
    [allowColumnResize, getWidth]
  )

  const resultFilterPills = useMemo(
    () => generateFilterPills(resultFilterTree, emailFilterFields),
    [resultFilterTree, emailFilterFields]
  )

  const activeResultFilterCount = useMemo(
    () => countFilterConditions(resultFilterTree),
    [resultFilterTree]
  )

  const hasActiveTextSearch = resultTextSearch.trim().length > 0
  const showingResultSubset =
    activeResultFilterCount > 0 ||
    hasActiveTextSearch ||
    sortedResults.length !== results.length

  const allVisibleRowsSelected = useMemo(
    () =>
      sortedResults.length > 0 &&
      sortedResults.every((r) => selectedIds.has(r.message_id)),
    [sortedResults, selectedIds]
  )

  function armSuppressCheckboxChangeForMessageId(messageId: string) {
    suppressCheckboxChangeForMessageIdRef.current = messageId
  }

  const handleResultRowClick = useCallback(
    (result: EmailSearchResult, index: number, e: MouseEvent) => {
      if (e.shiftKey && selectionAnchorIndexRef.current !== null) {
        const anchor = selectionAnchorIndexRef.current
        const lo = Math.min(anchor, index)
        const hi = Math.max(anchor, index)
        armSuppressCheckboxChangeForMessageId(result.message_id)
        patchSelectedIds((prev) => {
          const next = new Set(prev)
          for (const r of sortedResults.slice(lo, hi + 1)) {
            next.add(r.message_id)
          }
          return next
        })
      } else if (!e.shiftKey) {
        selectionAnchorIndexRef.current = index
      }
    },
    [sortedResults, patchSelectedIds]
  )

  const handleSelectionCheckboxMouseDown = useCallback(
    (result: EmailSearchResult, index: number, e: MouseEvent<HTMLInputElement>) => {
      e.stopPropagation()
      if (!e.shiftKey || selectionAnchorIndexRef.current === null) {
        return
      }
      e.preventDefault()
      const anchor = selectionAnchorIndexRef.current
      const lo = Math.min(anchor, index)
      const hi = Math.max(anchor, index)
      armSuppressCheckboxChangeForMessageId(result.message_id)
      patchSelectedIds((prev) => {
        const next = new Set(prev)
        for (const r of filteredResults.slice(lo, hi + 1)) {
          next.add(r.message_id)
        }
        return next
      })
    },
    [sortedResults, patchSelectedIds]
  )

  const handleSelectionCheckboxChange = useCallback(
    (result: EmailSearchResult, index: number, e: ChangeEvent<HTMLInputElement>) => {
      e.stopPropagation()
      if (suppressCheckboxChangeForMessageIdRef.current === result.message_id) {
        const suppressedId = result.message_id
        setTimeout(() => {
          if (suppressCheckboxChangeForMessageIdRef.current === suppressedId) {
            suppressCheckboxChangeForMessageIdRef.current = null
          }
        }, 0)
        return
      }
      selectionAnchorIndexRef.current = index
      const checked = e.target.checked
      patchSelectedIds((prev) => {
        const next = new Set(prev)
        if (checked) {
          next.add(result.message_id)
        } else {
          next.delete(result.message_id)
        }
        return next
      })
    },
    [patchSelectedIds]
  )

  function clearResultFilters() {
    setResultFilterTree(null)
  }

  function handleRemoveResultFilterPill(pillId: string) {
    setResultFilterTree(removeFilterPill(resultFilterTree, pillId))
  }

  function toggleSelectAll() {
    if (allVisibleRowsSelected) {
      setSelectedResultMessageIds([])
      selectionAnchorIndexRef.current = null
    } else {
      setSelectedResultMessageIds(Array.from(new Set(sortedResults.map((r) => r.message_id))))
      selectionAnchorIndexRef.current = sortedResults.length > 0 ? 0 : null
    }
  }

  const emailsForForwardStats = useMemo(() => {
    if (forwardDialogOverride && forwardDialogOverride.length > 0) {
      return forwardDialogOverride
    }
    return results.filter((r) => selectedIds.has(r.message_id))
  }, [forwardDialogOverride, results, selectedIds])

  const selectedEmailStats = useMemo(() => {
    const selected = emailsForForwardStats
    const alreadyProcessed = selected.filter((r) => processedInfo[r.message_id])
    const newEmails = selected.filter((r) => !processedInfo[r.message_id])
    const approved = alreadyProcessed.filter((r) => processedInfo[r.message_id]?.has_approved)
    const pending = alreadyProcessed.filter(
      (r) => processedInfo[r.message_id]?.has_pending && !processedInfo[r.message_id]?.has_approved
    )
    return { selected, alreadyProcessed, newEmails, approved, pending }
  }, [emailsForForwardStats, processedInfo])

  function handleForwardClick() {
    setForwardDialogOverride(null)
    if (selectedIds.size === 0) return
    if (selectedEmailStats.alreadyProcessed.length > 0) {
      setShowForwardDialog(true)
    } else {
      doProcess(selectedEmailStats.selected, false)
    }
  }

  function handleForwardRow(result: EmailSearchResult, e: MouseEvent) {
    e.stopPropagation()
    const scope = resolveForwardEmailScope(results, selectedIds, result)
    const hasAlreadyProcessed = scope.some((r) => processedInfo[r.message_id])
    if (hasAlreadyProcessed) {
      setForwardDialogOverride(scope)
      setShowForwardDialog(true)
    } else {
      setForwardDialogOverride(null)
      doProcess(scope, false)
    }
  }

  function handleForwardConfirm() {
    const includePreviouslyReviewed = shouldIncludePreviouslyReviewed(forwardNewOnly)
    const emailsToProcess = emailsToForwardFromDialog(
      emailsForForwardStats,
      processedInfo,
      forwardNewOnly
    )
    setShowForwardDialog(false)
    setForwardDialogOverride(null)
    doProcess(emailsToProcess, includePreviouslyReviewed)
  }

  async function doProcess(
    emailsToProcess: EmailSearchResult[],
    includePreviouslyReviewed: boolean
  ) {
    if (emailsToProcess.length === 0) {
      toast.success('No new emails to process (all selected emails were already reviewed)')
      setSelectedResultMessageIds([])
      selectionAnchorIndexRef.current = null
      return
    }

    try {
      setProcessing(true)

      const firstEmail = emailsToProcess[0]
      const parsingHintsEmailRuleId =
        emailsToProcess.length === 1 && firstEmail != null && firstEmail.email_rule_id != null
          ? firstEmail.email_rule_id
          : store.lastSearchUsedRules && store.selectedRuleIds.length === 1
            ? store.selectedRuleIds[0]
            : undefined

      const emails = emailsToProcess.map(e => ({
        gmail_id: e.gmail_id,
        inbox: e.inbox,
        ...(parsingHintsEmailRuleId != null
          ? { email_rule_id: parsingHintsEmailRuleId }
          : {}),
      }))

      const response = await startBulkEmailProcess(emails, {
        include_previously_reviewed: includePreviouslyReviewed,
      })

      registerServerJob({
        id: response.job_id,
        job_type: 'bulk_email_process',
        status: 'pending',
        total_items: response.total_items,
        processed_items: 0,
        successful_items: 0,
        failed_items: 0,
        created_at: new Date().toISOString(),
      })

      setSelectedResultMessageIds([])
      selectionAnchorIndexRef.current = null
    } catch (err) {
      toast.error('Failed to start email processing')
      console.error(err)
    } finally {
      setProcessing(false)
    }
  }

  async function handleProcess() {
    handleForwardClick()
  }

  function buildSearchRequestsFromRules(rules: EmailRule[], globals: GlobalFilters): EmailSearchRequest[] {
    return rules.map(rule => ({
      from: rule.from_filter || undefined,
      subject: rule.subject_contains || undefined,
      has_words: rule.email_has_words || undefined,
      has_attachment: rule.has_attachment || undefined,
      inbox: globals.inbox || undefined,
      start_date: globals.start_date || undefined,
      end_date: globals.end_date || undefined,
      limit: globals.limit,
    }))
  }

  function normalizedGlobalInbox(): string {
    const raw = globalFilters.inbox
    if (!raw.trim() || monitoredInboxes.length === 0) return raw
    return completeInboxFilterTokens(raw, monitoredInboxes)
  }

  async function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    setSelectedResultMessageIds([])
    selectionAnchorIndexRef.current = null
    setResultFilterTree(null)
    setResultTextSearch('')
    setSortSpecs(EMAIL_SEARCH_DEFAULT_SORT)
    beginNewSearch()

    if (searchMode === 'manual') {
      clearRuleSelection()
    }

    const inboxFilter = normalizedGlobalInbox()
    if (inboxFilter !== globalFilters.inbox) {
      setGlobalFilters((prev) => ({ ...prev, inbox: inboxFilter }))
    }

    try {
      setLoading(true)

      let allResults: EmailSearchResult[] = []
      let queriesUsed: string[] = []

      if (searchMode === 'manual') {
        const trimmedRaw = rawGmailQuery.trim()
        const request: EmailSearchRequest = trimmedRaw
          ? {
              query: trimmedRaw,
              inbox: inboxFilter || undefined,
              start_date: globalFilters.start_date || undefined,
              end_date: globalFilters.end_date || undefined,
              limit: globalFilters.limit,
            }
          : {
              from: manualFilters.from || undefined,
              to: manualFilters.to || undefined,
              subject: manualFilters.subject || undefined,
              has_words: manualFilters.has_words || undefined,
              doesnt_have: manualFilters.doesnt_have || undefined,
              has_attachment: manualFilters.has_attachment,
              inbox: inboxFilter || undefined,
              start_date: globalFilters.start_date || undefined,
              end_date: globalFilters.end_date || undefined,
              limit: globalFilters.limit,
            }
        const data = await searchEmails(request)
        allResults = data.results
        queriesUsed = [data.query_used]
      } else {
        const selectedRules = emailRules.filter(r => selectedRuleIds.has(r.id))
        if (selectedRules.length === 0) {
          toast.error('Please select at least one rule to search')
          setLoading(false)
          return
        }

        const seenMessageIds = new Set<string>()

        const globalsForSearch = { ...globalFilters, inbox: inboxFilter }
        for (const rule of selectedRules) {
          const requests = buildSearchRequestsFromRules([rule], globalsForSearch)
          for (const request of requests) {
            try {
              const data = await searchEmails(request)
              queriesUsed.push(data.query_used)
              for (const result of data.results) {
                if (!seenMessageIds.has(result.message_id)) {
                  seenMessageIds.add(result.message_id)
                  allResults.push({ ...result, email_rule_id: rule.id })
                }
              }
            } catch (err) {
              console.error('Error searching with rule:', err)
            }
          }
        }
      }

      const dedupedResults = dedupeEmailSearchResultsByMessageId(allResults)
      store.setResults(
        dedupedResults,
        queriesUsed.join('\n---\n'),
        searchMode === 'rules',
      )

      if (dedupedResults.length > 0) {
        const messageIds = dedupedResults.map(r => r.message_id)
        const processedData = await checkProcessedEmails(messageIds)
        setProcessedInfo(processedData.processed)
      }
    } catch (err) {
      toast.error('Search failed')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  function formatDate(dateStr: string): string {
    return formatLocalDateTime(dateStr, '')
  }

  function formatLastSearchTime(ts: number): string {
    return formatLocalDateTime(ts, '')
  }

  function getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'confirmed':
        return 'badge-success'
      case 'disabled':
        return 'badge-neutral'
      default:
        return 'badge-warning'
    }
  }

  const canSearch = searchMode === 'manual' || selectedRuleIds.size > 0

  function renderEmailHeaderCell(columnKey: EmailSearchColumnKey): ReactNode {
    if (columnKey === 'select') {
      return (
        <th key={columnKey} className="relative" style={headerStyle(columnKey)}>
          <input
            type="checkbox"
            checked={allVisibleRowsSelected}
            onChange={toggleSelectAll}
            className="rounded border-gray-300"
            aria-label="Select all visible rows"
          />
          {allowColumnResize ? (
            <ColumnResizeHandle columnKey={columnKey} onStartResize={startResize} />
          ) : null}
        </th>
      )
    }

    if (columnKey === 'email_status') {
      return (
        <th key={columnKey} className="relative" style={headerStyle(columnKey)}>
          <span className="inline-flex items-center pr-2">{EMAIL_SEARCH_COLUMN_LABELS[columnKey]}</span>
          {allowColumnResize ? (
            <ColumnResizeHandle columnKey={columnKey} onStartResize={startResize} />
          ) : null}
        </th>
      )
    }

    if (columnKey === 'row_actions') {
      return (
        <th key={columnKey} className="relative text-right" style={headerStyle(columnKey)}>
          <span className="sr-only">{EMAIL_SEARCH_COLUMN_LABELS[columnKey]}</span>
          {allowColumnResize ? (
            <ColumnResizeHandle columnKey={columnKey} onStartResize={startResize} />
          ) : null}
        </th>
      )
    }

    if (isEmailColumnSortable(columnKey)) {
      return (
        <EmailSortableHeader
          key={columnKey}
          label={EMAIL_SEARCH_COLUMN_LABELS[columnKey]}
          sortKey={columnKey}
          sortDirection={getSortDirection(columnKey)}
          onSort={handleRequestSort}
          columnKey={columnKey}
          style={headerStyle(columnKey)}
          onStartResize={startResize}
          showResize={allowColumnResize}
        />
      )
    }

    return null
  }

  function renderEmailCell(
    columnKey: EmailSearchColumnKey,
    result: EmailSearchResult,
    index: number
  ): ReactNode {
    const info = processedInfo[result.message_id]

    switch (columnKey) {
      case 'select':
        return (
          <td
            key={columnKey}
            style={cellStyle(columnKey)}
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="checkbox"
              checked={selectedIds.has(result.message_id)}
              onMouseDown={(e) => handleSelectionCheckboxMouseDown(result, index, e)}
              onChange={(e) => handleSelectionCheckboxChange(result, index, e)}
              className="rounded border-gray-300"
            />
          </td>
        )
      case 'inbox':
        return (
          <td
            key={columnKey}
            className="text-gray-500 truncate"
            style={cellStyle(columnKey)}
            title={result.inbox}
          >
            {result.inbox}
          </td>
        )
      case 'from_address':
        return (
          <td
            key={columnKey}
            className="text-gray-500 truncate"
            style={cellStyle(columnKey)}
            title={result.from_address}
          >
            {result.from_address}
          </td>
        )
      case 'date':
        return (
          <td
            key={columnKey}
            className="text-gray-500 truncate"
            style={cellStyle(columnKey)}
            title={formatDate(result.date)}
          >
            {formatDate(result.date)}
          </td>
        )
      case 'subject':
        return (
          <td
            key={columnKey}
            className="font-medium truncate"
            style={cellStyle(columnKey)}
            title={result.subject}
          >
            {result.subject}
          </td>
        )
      case 'preview':
        return (
          <td
            key={columnKey}
            className="truncate text-sm text-gray-500"
            style={cellStyle(columnKey)}
            title={result.snippet || undefined}
          >
            {result.snippet || '—'}
          </td>
        )
      case 'has_attachment':
        return (
          <td key={columnKey} style={cellStyle(columnKey)}>
            {result.has_attachment ? (
              <span className="badge badge-info">Yes</span>
            ) : (
              <span className="badge badge-neutral">No</span>
            )}
          </td>
        )
      case 'email_status':
        return (
          <td key={columnKey} style={cellStyle(columnKey)}>
            {info ? (
              info.has_approved ? (
                <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
                  <CheckCircleIcon className="w-3.5 h-3.5" />
                  Sent to QBO
                </span>
              ) : info.has_pending ? (
                <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                  <ClockIcon className="w-3.5 h-3.5" />
                  In Review
                </span>
              ) : (
                <span className="text-xs text-gray-400">Processed</span>
              )
            ) : (
              <span className="text-xs text-gray-300">New</span>
            )}
          </td>
        )
      case 'row_actions':
        return (
          <td
            key={columnKey}
            style={cellStyle(columnKey)}
            className="text-right"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="btn btn-ghost p-1.5 rounded-md text-blue-600 hover:bg-blue-50"
              title="Forward to Doc Review"
              aria-label="Forward to Doc Review"
              disabled={processing}
              onClick={(e) => handleForwardRow(result, e)}
            >
              <PaperAirplaneIcon className="w-4 h-4" />
            </button>
          </td>
        )
      default:
        return null
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex-1 flex flex-col md:flex-row gap-4 min-h-0 overflow-y-auto md:overflow-hidden">
        {/* Left sidebar with search filters */}
        <div className="w-full md:w-[24rem] lg:w-[27rem] flex-shrink-0 flex flex-col gap-3 md:overflow-y-auto">
          {/* Global Filters Card */}
          <div className="card flex-shrink-0">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
              Global Filters
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="label text-xs">Inbox</label>
                <InboxFilterInput
                  className="input-sm"
                  value={globalFilters.inbox}
                  onChange={(inbox) => setGlobalFilters((prev) => ({ ...prev, inbox }))}
                  placeholder="taylor, lauren"
                />
              </div>
              <div>
                <label className="label text-xs">Start Date</label>
                <DateInput
                  className="input-sm"
                  value={globalFilters.start_date}
                  onChange={(value) => setGlobalFilters(prev => ({ ...prev, start_date: value }))}
                  dateFormat="iso"
                />
              </div>
              <div>
                <label className="label text-xs">End Date</label>
                <DateInput
                  className="input-sm"
                  value={globalFilters.end_date}
                  onChange={(value) => setGlobalFilters(prev => ({ ...prev, end_date: value }))}
                  dateFormat="iso"
                />
              </div>
              <div className="col-span-2">
                <label className="label text-xs">Result Limit</label>
                <input
                  type="number"
                  className="input-sm"
                  min="1"
                  max="500"
                  value={globalFilters.limit || ''}
                  onChange={(e) => setGlobalFilters(prev => ({
                    ...prev,
                    limit: e.target.value ? parseInt(e.target.value, 10) : undefined,
                  }))}
                  placeholder="Max results per query; leave blank to see all results"
                />
              </div>
            </div>
          </div>

          {/* Search Filters Form */}
          <form onSubmit={handleSearch} className="flex flex-col gap-3">
            {/* Search Filters Card */}
            <div className="card">
              {/* Search Mode Tabs */}
              <div className="mb-3">
                <div className="flex border-b border-gray-200">
                <button
                  type="button"
                  onClick={() => setSearchMode('rules')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    searchMode === 'rules'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Search by Rules
                  {selectedRuleIds.size > 0 && (
                    <span className="ml-1.5 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
                      {selectedRuleIds.size}
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setSearchMode('manual')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    searchMode === 'manual'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Manual Query
                </button>
              </div>
            </div>

              {/* Tab Content */}
              <div>
              {searchMode === 'manual' ? (
                <div className="space-y-3">
                  <div>
                    <label className="label text-xs" htmlFor="email-raw-gmail-query">
                      <HoverHelp
                        label="Raw Gmail query (optional)"
                        aria-label="About Raw Gmail query"
                      >
                        <p>
                          When this box has text, it is sent to Gmail as-is and the fields below are
                          ignored. Leave it empty to build a query from From / Subject / Has the
                          words.
                        </p>
                      </HoverHelp>
                    </label>
                    <textarea
                      id="email-raw-gmail-query"
                      className="input-sm w-full min-h-[4.5rem] font-mono text-xs"
                      value={rawGmailQuery}
                      onChange={(e) => setRawGmailQuery(e.target.value)}
                      placeholder='Paste the same string you use in Gmail search, e.g. from:sender@x.com subject:("You paid!") "Cochinita"'
                      spellCheck={false}
                    />
                  </div>
                  <div>
                    <label className="label text-xs">From</label>
                    <input
                      type="text"
                      className="input-sm"
                      value={manualFilters.from}
                      onChange={(e) => setManualFilters(prev => ({ ...prev, from: e.target.value }))}
                      placeholder="sender@vendor.com"
                    />
                  </div>

                  <div>
                    <label className="label text-xs">To</label>
                    <input
                      type="text"
                      className="input-sm"
                      value={manualFilters.to}
                      onChange={(e) => setManualFilters(prev => ({ ...prev, to: e.target.value }))}
                      placeholder="recipient@example.com"
                    />
                  </div>

                  <div>
                    <label className="label text-xs">Subject</label>
                    <input
                      type="text"
                      className="input-sm"
                      value={manualFilters.subject}
                      onChange={(e) => setManualFilters(prev => ({ ...prev, subject: e.target.value }))}
                      placeholder="Contains these words..."
                    />
                  </div>

                  <div>
                    <label className="label text-xs">Has the words</label>
                    <input
                      type="text"
                      className="input-sm"
                      value={manualFilters.has_words}
                      onChange={(e) => setManualFilters(prev => ({ ...prev, has_words: e.target.value }))}
                      placeholder='"invoice receipt payment"'
                    />
                  </div>

                  <div>
                    <label className="label text-xs">Doesn't have</label>
                    <input
                      type="text"
                      className="input-sm"
                      value={manualFilters.doesnt_have}
                      onChange={(e) => setManualFilters(prev => ({ ...prev, doesnt_have: e.target.value }))}
                      placeholder='"unsubscribe newsletter"'
                    />
                  </div>

                  <div>
                    <label className="label text-xs">Has Attachment</label>
                    <select
                      className="input-sm select"
                      value={manualFilters.has_attachment === undefined ? '' : manualFilters.has_attachment ? 'yes' : 'no'}
                      onChange={(e) => setManualFilters(prev => ({
                        ...prev,
                        has_attachment: e.target.value === '' ? undefined : e.target.value === 'yes',
                      }))}
                    >
                      <option value="">Any</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Quick Filters */}
                  <div>
                    <div className="text-xs font-medium text-gray-600 mb-2">Quick Select</div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => handleQuickFilterChange('confirmed')}
                        className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                          quickFilter === 'confirmed'
                            ? 'bg-green-50 border-green-300 text-green-700'
                            : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}
                      >
                        All Confirmed
                      </button>
                      {uniqueDestinations.includes('qbo') && (
                        <button
                          type="button"
                          onClick={() => handleQuickFilterChange('confirmed-qbo')}
                          className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                            quickFilter === 'confirmed-qbo'
                              ? 'bg-purple-50 border-purple-300 text-purple-700'
                              : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                          }`}
                        >
                          Confirmed + QBO
                        </button>
                      )}
                      {uniqueDestinations.includes('meez') && (
                        <button
                          type="button"
                          onClick={() => handleQuickFilterChange('confirmed-meez')}
                          className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                            quickFilter === 'confirmed-meez'
                              ? 'bg-orange-50 border-orange-300 text-orange-700'
                              : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                          }`}
                        >
                          Confirmed + Meez
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Rule Search */}
                  <div>
                    <label className="label text-xs">Search Rules</label>
                    <input
                      type="text"
                      className="input-sm"
                      placeholder="Filter by vendor, from, subject..."
                      value={ruleSearch}
                      onChange={(e) => setRuleSearch(e.target.value)}
                    />
                  </div>

                  {/* Selection Summary */}
                  {selectedRuleIds.size > 0 && (
                    <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                      <span className="text-sm text-blue-700">
                        {selectedRuleIds.size} rule{selectedRuleIds.size !== 1 ? 's' : ''} selected
                      </span>
                      <button
                        type="button"
                        onClick={clearRuleSelection}
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                      >
                        Clear
                      </button>
                    </div>
                  )}

                  {/* Rules List */}
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    {rulesLoading ? (
                      <div className="p-4 text-center text-gray-500 text-sm">
                        Loading rules...
                      </div>
                    ) : filteredRules.length === 0 ? (
                      <div className="p-4 text-center text-gray-500 text-sm">
                        {ruleSearch ? `No rules match "${ruleSearch}"` : 'No rules available'}
                      </div>
                    ) : (
                      <div className="max-h-[280px] overflow-y-auto divide-y divide-gray-100">
                        {rulesForDisplay.map(rule => {
                          const isSelected = selectedRuleIds.has(rule.id)
                          const integrationDests = INTEGRATION_DEST_ORDER.filter((d) =>
                            rule.destinations.includes(d)
                          )
                          const otherDests = rule.destinations.filter(
                            (d) => d !== 'qbo' && d !== 'meez'
                          )
                          return (
                            <button
                              key={rule.id}
                              type="button"
                              onClick={() => toggleRuleSelection(rule.id)}
                              className={`w-full text-left px-3 py-2 transition-colors ${
                                isSelected
                                  ? 'bg-blue-50'
                                  : 'hover:bg-gray-50'
                              }`}
                            >
                              <div className="flex items-start gap-2">
                                <div className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                                  isSelected
                                    ? 'bg-blue-500 border-blue-500'
                                    : 'border-gray-300'
                                }`}>
                                  {isSelected && <CheckIcon className="w-3 h-3 text-white" />}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-medium text-sm text-gray-900 truncate">
                                      {rule.vendor_name}
                                    </span>
                                    <span className={`badge text-[10px] shrink-0 ${getStatusBadgeClass(rule.status)}`}>
                                      {rule.status}
                                    </span>
                                    {integrationDests.map((dest) => (
                                      <span key={dest} className="badge badge-info text-[10px] shrink-0">
                                        {dest}
                                      </span>
                                    ))}
                                  </div>
                                  <div className="text-xs text-gray-500 mt-0.5 truncate">
                                    {rule.from_filter && <span>from: {rule.from_filter}</span>}
                                    {rule.from_filter && rule.subject_contains && <span> · </span>}
                                    {rule.subject_contains && <span>subj: {rule.subject_contains.slice(0, 30)}{rule.subject_contains.length > 30 ? '...' : ''}</span>}
                                    {!rule.from_filter && !rule.subject_contains && <span className="text-gray-400">No filters</span>}
                                  </div>
                                  {otherDests.length > 0 && (
                                    <div className="flex gap-1 mt-1 flex-wrap">
                                      {otherDests.map((dest) => (
                                        <span key={dest} className="badge badge-info text-[10px]">
                                          {dest}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </button>
                          )
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
              </div>
            </div>

            {/* Search Button */}
            <button
              type="submit"
              disabled={loading || !canSearch}
              className="btn btn-primary w-full"
            >
              <MagnifyingGlassIcon className="w-4 h-4" />
              {loading ? 'Searching...' : searchMode === 'rules' && selectedRuleIds.size > 0
                ? `Search ${selectedRuleIds.size} Rule${selectedRuleIds.size !== 1 ? 's' : ''}`
                : 'Search'}
            </button>
          </form>
        </div>

        {/* Results panel */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col max-md:min-h-[42vh]">
          {queryUsed && (
            <details className="flex-shrink-0 text-sm text-gray-500 mb-3">
              <summary className="cursor-pointer hover:text-gray-700">
                Show {searchMode === 'rules' && selectedRuleIds.size > 1 ? 'queries' : 'query'} used
              </summary>
              <code className="block mt-2 bg-gray-100 px-3 py-2 rounded text-xs overflow-x-auto whitespace-pre-wrap break-all">
                {queryUsed}
              </code>
            </details>
          )}

          {results.length > 0 ? (
            <TablePageShell
              className="min-h-0 flex-1"
              rowCount={
                <div className="mb-2 flex flex-wrap items-center gap-x-1.5 text-xs text-gray-500 sm:text-sm">
                  <span>
                    <span className="font-medium text-gray-700">
                      {showingResultSubset
                        ? `${sortedResults.length} result${sortedResults.length !== 1 ? 's' : ''} shown`
                        : `${results.length} result${results.length !== 1 ? 's' : ''}`}
                    </span>
                    {showingResultSubset ? (
                      <span className="ml-1">(of {results.length.toLocaleString()} total)</span>
                    ) : null}
                  </span>
                  {selectedIds.size > 0 ? <span>· {selectedIds.size} selected</span> : null}
                </div>
              }
              toolbarLeft={
                <SearchInput
                  value={resultTextSearch}
                  onChange={setResultTextSearch}
                  placeholder="Filter results…"
                  className="max-w-md flex-1"
                />
              }
              toolbarRight={
                <div className="flex flex-wrap items-center gap-2">
                  {selectedIds.size > 0 ? (
                    <button
                      type="button"
                      onClick={handleProcess}
                      disabled={processing}
                      className="btn btn-primary text-sm"
                    >
                      <PaperAirplaneIcon className="h-4 w-4" />
                      {processing ? 'Starting...' : `Forward ${selectedIds.size}`}
                    </button>
                  ) : null}
                  <TableToolbar
                    filterCount={activeResultFilterCount}
                    sortCount={sortSpecs.length}
                    showFilters={showResultFilters}
                    showSort={showSort}
                    showColumns={showColumns}
                    onToggleFilters={() => setShowResultFilters((open) => !open)}
                    onToggleSort={() => setShowSort((open) => !open)}
                    onToggleColumns={() => setShowColumns((open) => !open)}
                  />
                </div>
              }
              filterPills={
                <ActiveFilterPillBar
                  pills={resultFilterPills}
                  onRemovePill={handleRemoveResultFilterPill}
                  onClearAll={clearResultFilters}
                />
              }
            >
              <div className="table-scroll-mobile min-h-0 overflow-x-auto overflow-y-auto overscroll-x-contain max-md:min-h-[40vh]">
                <table
                  className="table-forecast border-separate border-spacing-0"
                  style={tableStyle}
                >
                    <thead>
                    <tr>
                      {visibleColumnKeys.map((columnKey) =>
                        renderEmailHeaderCell(columnKey as EmailSearchColumnKey)
                      )}
                    </tr>
                  </thead>
                    <tbody>
                      {sortedResults.length === 0 ? (
                        <tr>
                          <td
                            colSpan={visibleColumnCount}
                            className="py-12 text-center text-sm text-gray-600 px-4"
                          >
                            No rows match your table filters.{' '}
                            <button
                              type="button"
                              className="font-medium text-blue-600 hover:underline"
                              onClick={() => {
                                clearResultFilters()
                                setResultTextSearch('')
                              }}
                            >
                              Clear filters
                            </button>
                          </td>
                        </tr>
                      ) : null}
                      {sortedResults.map((result, index) => (
                          <tr
                            key={`${result.gmail_id}:${result.inbox}`}
                            onClick={(e) => handleResultRowClick(result, index, e)}
                            className={`cursor-pointer ${selectedIds.has(result.message_id) ? 'bg-blue-50' : ''}`}
                          >
                            {visibleColumnKeys.map((columnKey) =>
                              renderEmailCell(columnKey as EmailSearchColumnKey, result, index)
                            )}
                          </tr>
                        ))}
                    </tbody>
                  </table>
              </div>
            </TablePageShell>
          ) : loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center text-gray-500">
                <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-3"></div>
                <p className="text-sm font-medium">Searching emails…</p>
                <p className="text-xs text-gray-400 mt-2">Results will appear here when the search completes.</p>
              </div>
            </div>
          ) : lastSearchCompletedAt != null && results.length === 0 ? (
            <div className="flex-1 flex items-center justify-center px-4">
              <div className="max-w-md text-center">
                <div className="rounded-full bg-amber-50 w-14 h-14 flex items-center justify-center mx-auto mb-4">
                  <MagnifyingGlassIcon className="w-8 h-8 text-amber-600" />
                </div>
                <h3 className="text-base font-semibold text-gray-900">No messages matched</h3>
                <p className="text-sm text-gray-600 mt-2">
                  Your search finished at{' '}
                  <time dateTime={new Date(lastSearchCompletedAt).toISOString()}>
                    {formatLastSearchTime(lastSearchCompletedAt)}
                  </time>{' '}
                  with <strong>0 results</strong>. Try widening the date range, adjusting rules, or changing your
                  query, then click <strong>Search</strong> again.
                </p>
              </div>
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center px-4">
              <div className="max-w-sm text-center text-gray-400">
                <MagnifyingGlassIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="text-sm text-gray-600">
                  Run a search to see results here.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {results.length > 0 ? (
        <FilterBuilderDrawer
          open={showResultFilters}
          onClose={() => setShowResultFilters(false)}
          fields={emailFilterFields}
          root={resultFilterTree}
          onChange={setResultFilterTree}
          onClearAll={() => setResultFilterTree(null)}
        />
      ) : null}

      <SortBuilder
        open={showSort}
        fields={EMAIL_SEARCH_FIELD_DEFINITIONS}
        sorts={sortSpecs}
        onChange={setSortSpecs}
        onClose={() => setShowSort(false)}
        onClearAll={() => setSortSpecs(EMAIL_SEARCH_DEFAULT_SORT)}
      />

      <ColumnPicker
        open={showColumns}
        fields={EMAIL_SEARCH_FIELD_DEFINITIONS}
        visibility={columnVisibility}
        onChange={handleColumnVisibilityChange}
        onClose={() => setShowColumns(false)}
        onReset={() => {
          handleColumnVisibilityChange(getDefaultColumnVisibility(EMAIL_SEARCH_FIELD_DEFINITIONS))
        }}
      />

      {/* Forward Confirmation Dialog */}
      {showForwardDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => {
              setShowForwardDialog(false)
              setForwardDialogOverride(null)
            }}
          />
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full max-h-[95vh] overflow-y-auto p-4 sm:p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Forward to Doc Review
            </h3>

            <div className="space-y-3 mb-6">
              <p className="text-sm text-gray-600">
                You've selected <strong>{selectedEmailStats.selected.length}</strong> email{selectedEmailStats.selected.length !== 1 ? 's' : ''}:
              </p>

              <div className="bg-gray-50 rounded-lg p-3 space-y-2 text-sm">
                {selectedEmailStats.newEmails.length > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-medium flex-shrink-0">
                      {selectedEmailStats.newEmails.length}
                    </span>
                    <span className="text-gray-700">New (not previously reviewed)</span>
                  </div>
                )}
                {selectedEmailStats.pending.length > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-xs font-medium flex-shrink-0">
                      {selectedEmailStats.pending.length}
                    </span>
                    <span className="text-gray-700">Already in Doc Review queue</span>
                  </div>
                )}
                {selectedEmailStats.approved.length > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-xs font-medium flex-shrink-0">
                      {selectedEmailStats.approved.length}
                    </span>
                    <span className="text-gray-700">Already sent to QBO</span>
                  </div>
                )}
              </div>

              <div className="pt-2">
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  Which emails should be forwarded?
                </label>
                <div className="space-y-3">
                  <label className="flex items-start gap-3 cursor-pointer p-2 -mx-2 rounded-lg hover:bg-gray-50">
                    <input
                      type="radio"
                      name="forwardOption"
                      checked={forwardNewOnly}
                      onChange={() => setForwardNewOnly(true)}
                      className="mt-0.5"
                    />
                    <div>
                      <span className="text-sm text-gray-900 font-medium">
                        New emails only
                      </span>
                      <span className="text-sm text-gray-500 block">
                        Skip {selectedEmailStats.alreadyProcessed.length} already-reviewed
                      </span>
                    </div>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer p-2 -mx-2 rounded-lg hover:bg-gray-50">
                    <input
                      type="radio"
                      name="forwardOption"
                      checked={!forwardNewOnly}
                      onChange={() => setForwardNewOnly(false)}
                      className="mt-0.5"
                    />
                    <div>
                      <span className="text-sm text-gray-900 font-medium">
                        All selected emails
                      </span>
                      <span className="text-sm text-gray-500 block">
                        Include previously reviewed
                      </span>
                    </div>
                  </label>
                </div>
              </div>
            </div>

            <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 sm:justify-end">
              <button
                type="button"
                onClick={() => {
                  setShowForwardDialog(false)
                  setForwardDialogOverride(null)
                }}
                className="btn btn-ghost w-full sm:w-auto"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleForwardConfirm}
                className="btn btn-primary w-full sm:w-auto"
                disabled={forwardNewOnly && selectedEmailStats.newEmails.length === 0}
              >
                <PaperAirplaneIcon className="w-4 h-4" />
                Forward {forwardNewOnly ? selectedEmailStats.newEmails.length : selectedEmailStats.selected.length}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
